/**
 * LoginPage — email + password against `POST /api/auth/login`.
 *
 * The backend sets an HttpOnly session cookie and a readable CSRF cookie; the
 * axios client in `lib/api` attaches the CSRF header on every unsafe method.
 * There is no token in localStorage by design — see `lib/api` for why the
 * client falls back to same-origin requests when BACKEND_URL is cross-origin.
 */
import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiErrorMessage } from "../lib/api";

const HOME = "/app/production/listicle-builder";

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!loading && user) {
    const dest = location.state?.from?.pathname || HOME;
    return <Navigate to={dest} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await login(email.trim(), password);
      navigate(location.state?.from?.pathname || HOME, { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, "Sign-in failed"));
    } finally {
      setBusy(false);
    }
  };

  const field =
    "h-10 w-full rounded-md border border-border bg-transparent px-3 text-sm outline-none focus:ring-1 focus:ring-[hsl(var(--slash-green))]";

  return (
    <div className="fos-login-grid flex min-h-screen items-center justify-center bg-background px-4 text-foreground">
      <form onSubmit={submit} className="w-full max-w-[340px]" data-testid="login-form">
        <h1 className="mb-1 text-lg font-medium tracking-tight">Sign in</h1>
        <p className="mb-6 text-2xs text-muted-foreground">Funnel OS workspace</p>

        <label className="mb-1.5 block text-2xs text-muted-foreground" htmlFor="login-email">
          Email
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={field}
          data-testid="login-email"
        />

        <label className="mb-1.5 mt-4 block text-2xs text-muted-foreground" htmlFor="login-password">
          Password
        </label>
        <input
          id="login-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={field}
          data-testid="login-password"
        />

        {error ? (
          <p className="mt-3 text-2xs text-red-500" role="alert" data-testid="login-error">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={busy}
          data-testid="login-submit"
          className="mt-5 inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-foreground text-sm font-medium text-background disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
